import React, { useEffect, useRef, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, formatRupiah } from "@/lib/api";
import { Printer, Bluetooth, Check, X, Clock, RefreshCw, Volume2, VolumeX, Bell, Star } from "lucide-react";
import { connectPrinter, isBluetoothSupported, isPrinterConnected, printReceipt } from "@/lib/printer";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Menunggu Pembayaran", color: "bg-[#D4A373]/20 text-[#8a5a1f]" },
  paid: { label: "Terbayar", color: "bg-[#3A7D44]/10 text-[#3A7D44]" },
  completed: { label: "Selesai", color: "bg-[#1E3A2B]/10 text-[#1E3A2B]" },
  cancelled: { label: "Dibatalkan", color: "bg-[#D64045]/10 text-[#D64045]" },
};

// Ring with Web Audio API — a short double-beep bell
function playBell() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    [0, 0.18].forEach((off) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.setValueAtTime(880, now + off);
      osc.frequency.exponentialRampToValueAtTime(1760, now + off + 0.08);
      gain.gain.setValueAtTime(0.0001, now + off);
      gain.gain.exponentialRampToValueAtTime(0.4, now + off + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + off + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + off);
      osc.stop(now + off + 0.35);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch {}
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [calls, setCalls] = useState([]);
  const [filter, setFilter] = useState("");
  const [printerName, setPrinterName] = useState(null);
  const [autoPrinted, setAutoPrinted] = useState({});
  const [settings, setSettings] = useState(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("kasir_sound") !== "0");
  const prevIdsRef = useRef(null);
  const prevCallIdsRef = useRef(null);
  const firstLoadRef = useRef(true);

  const load = async () => {
    const q = filter ? `?status=${filter}` : "";
    const [ordersRes, callsRes] = await Promise.all([
      api.get(`/orders${q}`),
      api.get("/calls?status=pending").catch(() => ({ data: [] })),
    ]);
    const data = ordersRes.data;
    const callsData = callsRes.data;
    const nowIds = new Set(data.map((o) => o.id));
    const prev = prevIdsRef.current;
    const nowCallIds = new Set(callsData.map((c) => c.id));
    const prevCallIds = prevCallIdsRef.current;

    if (prev && !firstLoadRef.current) {
      const newOrders = data.filter((o) => !prev.has(o.id));
      const newCalls = callsData.filter((c) => prevCallIds && !prevCallIds.has(c.id));
      if ((newOrders.length > 0 || newCalls.length > 0)) {
        if (soundOn) playBell();
        if (navigator.vibrate) navigator.vibrate([200, 80, 200]);
      }
      if (isPrinterConnected() && settings) {
        for (const o of newOrders) {
          if (!autoPrinted[o.id] && (o.status === "paid" || o.status === "pending")) {
            try {
              await printReceipt(o, settings);
              setAutoPrinted((a) => ({ ...a, [o.id]: true }));
            } catch (e) { /* ignore */ }
          }
        }
      }
    }
    firstLoadRef.current = false;
    prevIdsRef.current = nowIds;
    prevCallIdsRef.current = nowCallIds;
    setOrders(data);
    setCalls(callsData);
  };

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 7000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [filter, settings, soundOn]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("kasir_sound", next ? "1" : "0");
    if (next) playBell();
  };

  const dismissCall = async (c) => {
    await api.put(`/calls/${c.id}/dismiss`);
    toast.success("Panggilan diselesaikan");
    load();
  };

  const CALL_LABELS = { waiter: "Pelayan", water: "Minta Air", bill: "Minta Bon", other: "Lainnya" };

  const connect = async () => {
    try {
      const name = await connectPrinter();
      setPrinterName(name);
      toast.success(`Printer terhubung: ${name}`);
    } catch (e) {
      toast.error(e.message || "Gagal menghubungkan printer");
    }
  };

  const doPrint = async (o) => {
    try {
      await printReceipt(o, settings);
      toast.success("Struk dicetak");
    } catch (e) {
      toast.error(e.message || "Gagal cetak");
    }
  };

  const updateStatus = async (o, status) => {
    await api.put(`/orders/${o.id}/status?status=${status}`);
    toast.success("Status diperbarui");
    load();
  };

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">Pesanan</h1>
          <p className="text-[#5C6A61] mt-1">Pesanan masuk otomatis diperbarui setiap beberapa detik</p>
        </div>
        <div className="flex gap-2 items-center">
          <button
            data-testid="btn-toggle-sound"
            onClick={toggleSound}
            className={`px-3 py-2 rounded-xl text-sm inline-flex items-center gap-2 ${soundOn ? "bg-[#3A7D44] text-white" : "bg-[#EAE7DF] text-[#1E3A2B]"}`}
            title={soundOn ? "Notifikasi suara aktif" : "Notifikasi suara mati"}
          >
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            {soundOn ? "Suara On" : "Suara Off"}
          </button>
          <button data-testid="btn-refresh-orders" onClick={load} className="bg-[#EAE7DF] text-[#1E3A2B] px-3 py-2 rounded-xl text-sm inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            data-testid="btn-connect-printer"
            onClick={connect}
            disabled={!isBluetoothSupported()}
            className={`px-3 py-2 rounded-xl text-sm inline-flex items-center gap-2 ${isPrinterConnected() ? "bg-[#3A7D44] text-white" : "bg-[#1E3A2B] text-white"} disabled:opacity-50`}
          >
            <Bluetooth className="w-4 h-4" />
            {isPrinterConnected() ? `Terhubung: ${printerName || "Printer"}` : "Hubungkan Printer"}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { v: "", l: "Semua" },
          { v: "pending", l: "Menunggu" },
          { v: "paid", l: "Terbayar" },
          { v: "completed", l: "Selesai" },
          { v: "cancelled", l: "Dibatalkan" },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`filter-${f.v || "all"}`}
            onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-full text-xs ${filter === f.v ? "bg-[#1E3A2B] text-white" : "bg-white border border-[#D3CEC4] text-[#1E3A2B]"}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {/* Service Calls */}
      {calls.length > 0 && (
        <div className="mb-5 bg-[#D4A373]/15 border border-[#D4A373] rounded-md p-4" data-testid="calls-banner">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-[#8a5a1f]" />
            <span className="font-display font-semibold text-[#8a5a1f]">{calls.length} Panggilan Aktif</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {calls.map((c) => (
              <div key={c.id} data-testid={`call-${c.id}`} className="bg-white rounded-md border border-[#D4A373] px-3 py-2 flex items-center gap-3">
                <div>
                  <div className="text-sm font-medium text-[#1E3A2B]">Meja {c.table_number} · {CALL_LABELS[c.type] || c.type}</div>
                  <div className="text-[10px] text-[#5C6A61]">{new Date(c.created_at).toLocaleTimeString("id-ID")}</div>
                </div>
                <button
                  data-testid={`btn-dismiss-call-${c.id}`}
                  onClick={() => dismissCall(c)}
                  className="bg-[#3A7D44] text-white px-2.5 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
                >
                  <Check className="w-3 h-3" /> Selesai
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4" data-testid="orders-list">
        {orders.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#5C6A61] border border-dashed border-[#D3CEC4] rounded-md">
            Belum ada pesanan.
          </div>
        )}
        {orders.map((o) => {
          const meta = STATUS_META[o.status] || STATUS_META.pending;
          const isNew = o.status === "paid" && !autoPrinted[o.id];
          return (
            <div key={o.id} data-testid={`order-${o.id}`} className={`bg-white rounded-md border border-[#D3CEC4] p-5 ${isNew ? "pulse-ring border-[#C25934]" : ""}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <div className="font-display font-semibold text-[#1E3A2B]">#{o.order_no}</div>
                  <div className="text-xs text-[#5C6A61]">Meja {o.table_number} · {new Date(o.created_at).toLocaleString("id-ID")}</div>
                  {o.customer_name && <div className="text-xs text-[#5C6A61]">a.n. {o.customer_name}{o.customer_phone ? ` · ${o.customer_phone}` : ""}</div>}
                  {o.rating ? (
                    <div className="flex items-center gap-0.5 mt-1" title={o.rating_comment || ""}>
                      {[1,2,3,4,5].map((n) => <Star key={n} className={`w-3 h-3 ${o.rating >= n ? "fill-[#D4A373] text-[#D4A373]" : "text-[#D3CEC4]"}`} />)}
                      {o.rating_comment && <span className="text-[10px] text-[#5C6A61] italic ml-1 truncate max-w-[200px]">"{o.rating_comment}"</span>}
                    </div>
                  ) : null}
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${meta.color}`}>{meta.label}</span>
              </div>
              <div className="space-y-1.5 text-sm">
                {o.items.map((it, idx) => (
                  <div key={idx} className="flex items-start justify-between">
                    <div>
                      <div className="text-[#1E3A2B]">{it.quantity}× {it.name}</div>
                      {it.options?.length > 0 && <div className="text-xs text-[#5C6A61]">{it.options.map((op) => op.name).join(", ")}</div>}
                      {it.notes && <div className="text-xs text-[#C25934] italic">Catatan: {it.notes}</div>}
                    </div>
                    <div className="text-[#1E3A2B]">{formatRupiah((it.unit_price + (it.options || []).reduce((a, x) => a + x.price_delta, 0)) * it.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-3 mt-3 border-t border-[#EAE7DF]">
                <div>
                  {o.discount > 0 && (
                    <div className="text-xs text-[#3A7D44] mb-0.5">
                      Promo {o.promo?.code ? `(${o.promo.code})` : ""}: -{formatRupiah(o.discount)}
                    </div>
                  )}
                  <div className="font-display font-semibold text-[#C25934] text-lg">{formatRupiah(o.total)}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid={`btn-print-${o.id}`} onClick={() => doPrint(o)} className="bg-[#1E3A2B] text-white px-3 py-2 rounded-xl text-sm inline-flex items-center gap-1.5">
                    <Printer className="w-4 h-4" /> Cetak
                  </button>
                  {o.status === "pending" && (
                    <button data-testid={`btn-mark-paid-${o.id}`} onClick={() => updateStatus(o, "paid")} className="bg-[#3A7D44] text-white px-3 py-2 rounded-xl text-sm inline-flex items-center gap-1.5">
                      <Check className="w-4 h-4" /> Tandai Bayar
                    </button>
                  )}
                  {o.status === "paid" && (
                    <button data-testid={`btn-complete-${o.id}`} onClick={() => updateStatus(o, "completed")} className="bg-[#C25934] text-white px-3 py-2 rounded-xl text-sm">Selesai</button>
                  )}
                  {o.status !== "cancelled" && o.status !== "completed" && (
                    <button data-testid={`btn-cancel-${o.id}`} onClick={() => updateStatus(o, "cancelled")} className="text-[#D64045] px-2 py-2 rounded-xl text-sm">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </OwnerLayout>
  );
}
